import React, { useCallback, useEffect, useState } from 'react';
import styles from 'new/components/settings/SecurityNPrivacy/SecurityNPrivacy.module.scss';
import SettingsFormLayout, { SettingsBlock } from 'new/components/settings/settingsFormLayout';
import { PasswordTextField } from 'new/elements/PasswordTextField';
import { Button, CircularProgress } from '@mui/material';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { getCurrentUser } from 'js/reducers/selectors';
import {
  CHANGE_PASSWORD_REQUEST,
  changePassword,
  CHECK_PASSWORD_REQUEST,
  checkPasswordStrength,
} from 'new/stores/changePassword/action';
import { showAlert } from 'new/stores/showAlert/action';
import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import { useDebouncedCallback } from 'new/hooks/useDebouncedCallback';
import { logout } from 'js/actions';
import { StringUtil } from 'new/utils/StringUtil';

const { capitalizeArrayOfString } = StringUtil;

export function UpdatePassword() {
  const {
    register,
    watch,
    formState: { errors, isValid, dirtyFields },
    getValues,
    handleSubmit,
  } = useForm({
    mode: 'onChange',
    criteriaMode: 'all',
  });

  const [checked, setChecked] = useState(false);
  const dispatch = useDispatch<ThunkDispatch<any, any, Action>>();
  const user = useSelector(getCurrentUser);
  const entropyErrors = useSelector((state: any) => state.changePassword?.entropy?.errors);
  const checkingPassword = useIsLoading(CHECK_PASSWORD_REQUEST);
  const changePasswordLoading = useSelector((state: any) => state.request[CHANGE_PASSWORD_REQUEST]);

  const password = watch().password;

  const onSubmit = useCallback(
    (data) => {
      const { password, password_confirmation } = data;
      dispatch(
        changePassword({
          password,
          password_confirmation,
          userId: user.id,
        }),
      ).then(() => {
        dispatch(
          showAlert({
            severity: 'success',
            title: 'You have successfully updated your password. You need to login again.',
          }),
        );
        dispatch(logout(true));
      });
    },
    [user.id, dispatch],
  );

  const minLengthRule = dirtyFields.password && !errors?.password?.types['minLength'];
  const hasAlphabeticalLowerRule =
    dirtyFields.password && !errors?.password?.types['hasAlphabeticalLower'];
  const hasAlphabeticalUpperRule =
    dirtyFields.password && !errors?.password?.types['hasAlphabeticalUpper'];
  const hasNumberRule = dirtyFields.password && !errors?.password?.types['hasNumber'];
  const hasSpecialCharRule = dirtyFields.password && !errors?.password?.types['hasSpecialChar'];
  const isStrongEnough = !!entropyErrors && !entropyErrors.length;

  const onKeyUp = (e) => {
    if (['Enter', 'enter'].includes(e.key)) {
      dispatch(checkPasswordStrength(password)).finally(() => {
        setChecked(true);
      });
    }
  };

  const checkPasswordEffect = useDebouncedCallback(
    () => {
      if (
        minLengthRule &&
        hasAlphabeticalUpperRule &&
        hasAlphabeticalLowerRule &&
        hasNumberRule &&
        hasSpecialCharRule &&
        !checked &&
        !checkingPassword
      ) {
        dispatch(checkPasswordStrength(password)).finally(() => {
          setChecked(true);
        });
      }
    },
    [
      password,
      checkingPassword,
      checked,
      minLengthRule,
      hasAlphabeticalUpperRule,
      hasAlphabeticalLowerRule,
      hasNumberRule,
      hasSpecialCharRule,
    ],
    500,
  );

  useEffect(checkPasswordEffect, [checkPasswordEffect]);

  return (
    <form className={styles.formUpdatePassword} onSubmit={handleSubmit(onSubmit)}>
      <SettingsFormLayout>
        <SettingsBlock
          label="Update password"
          description={
            <div>
              Password must meet the following requirements:
              <ol className={styles.passwordOLList}>
                <li className={minLengthRule ? styles.green : ''}>
                  Be a minimum of 8 characters in length.
                </li>
                <li
                  className={
                    hasSpecialCharRule &&
                    hasNumberRule &&
                    hasAlphabeticalLowerRule &&
                    hasAlphabeticalUpperRule
                      ? styles.green
                      : ''
                  }
                >
                  Include at least one of each:
                  <ul>
                    <li className={hasAlphabeticalUpperRule ? styles.green : ''}>
                      Upper case alphabetical letter (A-Z)
                    </li>
                    <li className={hasAlphabeticalLowerRule ? styles.green : ''}>
                      Lower case alphabetical letter (a-z)
                    </li>
                    <li className={hasNumberRule ? styles.green : ''}>Numeral (0-9)</li>
                    <li className={hasSpecialCharRule ? styles.green : ''}>
                      Non-alphanumeric character (symbol or punctuation mark)
                    </li>
                  </ul>
                </li>
                <li className={isStrongEnough && checked ? styles.green : ''}>
                  {checkingPassword ? (
                    <CircularProgress size={12} className={styles.passwordCheckLoader} />
                  ) : null}
                  Password should be strong enough to not be easily guessable
                </li>
              </ol>
            </div>
          }
        >
          {changePasswordLoading ? (
            <CircularProgress />
          ) : (
            <div className={styles.newPasswordContainer}>
              <PasswordTextField
                label="New password"
                onKeyUp={onKeyUp}
                autoComplete="new-password"
                error={!!errors.password || (checked && entropyErrors?.length > 0)}
                helperText={
                  errors.password || (checked && entropyErrors?.length > 0)
                    ? errors.password?.message || capitalizeArrayOfString(entropyErrors).join(' - ')
                    : undefined
                }
                {...register('password', {
                  onChange: () => setChecked(false),
                  required: 'Please, input the password',
                  validate: {
                    minLength: (value) => {
                      return value.length >= 8 || 'Password needs to have at least 8 characters';
                    },
                    hasAlphabeticalLower: (value) => {
                      return (
                        /[a-z]/.test(value) ||
                        'Needs to have at least one lower case alphabetical character'
                      );
                    },
                    hasAlphabeticalUpper: (value) => {
                      return (
                        /[A-Z]/.test(value) ||
                        'Needs to have at least one upper case alphabetical character'
                      );
                    },
                    hasNumber: (value) => {
                      return /\d/.test(value) || 'Needs to have at least one number';
                    },
                    hasSpecialChar: (value) => {
                      return (
                        /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(value) ||
                        'Needs to have at least one special character'
                      );
                    },
                  },
                })}
              />
              <PasswordTextField
                label="Confirm new password"
                error={!!errors.password_confirmation}
                helperText={
                  errors.password_confirmation ? (
                    <>{errors.password_confirmation?.message}</>
                  ) : undefined
                }
                {...register('password_confirmation', {
                  required: 'Please, input the password confirmation',
                  validate: {
                    equalsConfirmation: (value) =>
                      value === getValues().password ||
                      'Confirmation password needs to be equal to password',
                  },
                })}
              />
              <Button
                type="submit"
                variant="contained"
                disabled={!isValid || !isStrongEnough || !checked}
                className={styles.changeButton}
              >
                Change password
              </Button>
            </div>
          )}
        </SettingsBlock>
      </SettingsFormLayout>
    </form>
  );
}
